import React, { useEffect, useRef, useState } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Typography from '@mui/material/Typography'
import Add from '@mui/icons-material/Add'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX, getFormChoiceCardSx } from '../../component/formSurfaceStyles'
import { getLocationState } from '../../utils/listPageState'
import {
    applyAppRankingBannerDraft,
    applyAppRankingBannerFieldChange,
    APP_RANKING_BANNER_EXPOSE_REGION_OPTIONS,
    APP_RANKING_BANNER_EXPOSE_REGIONS,
    APP_RANKING_BANNER_IMAGE_FILE_ACCEPT,
    APP_RANKING_BANNER_IMAGE_FILE_ERROR_MESSAGE,
    APP_RANKING_BANNER_IMAGE_SECTION_CONFIGS,
    clearAppRankingBannerDraft,
    createAppRankingBannerDraftPayload,
    createDefaultAppRankingBannerParams,
    handleAppRankingBannerBackNavigation,
    handleAppRankingBannerImageUpload,
    moveToAppRankingBannerList,
    NEW_APP_RANKING_BANNER_DRAFT_KEY,
    prepareAppRankingBannerSubmit,
    readAppRankingBannerDraft,
    saveAppRankingBannerDraft,
} from './appRankingBannerDraftUtils'
import AppRankingBannerRewardFields from './AppRankingBannerRewardFields'
import {
    APP_RANKING_BANNER_IMAGE_LEAVE_MESSAGE,
    hasPendingAppRankingBannerImage,
    usePendingAppRankingBannerImageUnloadGuard,
} from './appRankingBannerLeaveGuard'

const PANEL_SX = FORM_PANEL_SX

const IMAGE_PREVIEW_FRAME_SX = {
    ...FORM_IMAGE_PREVIEW_FRAME_SX,
    width: '100%',
    height: { xs: 220, md: 280 },
    mb: 1.5,
}

const IMAGE_PREVIEW_SX = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
}

const getChoiceCardSx = (selected, disabled = false) => ({
    ...getFormChoiceCardSx(selected, disabled),
    p: 1.5,
    width: '100%',
})

const RANKING_BANNER_TYPE_OPTIONS = [
    {
        value: 1,
        title: '러닝 주간',
        description: '주간 러닝 랭킹 기준으로 경품을 노출합니다.',
    },
    {
        value: 2,
        title: '러닝 월간',
        description: '월간 러닝 랭킹 기준으로 경품을 노출합니다.',
    },
    {
        value: 3,
        title: '만보기 주간',
        description: '주간 만보기 랭킹 기준으로 경품을 노출합니다.',
    },
    {
        value: 4,
        title: '만보기 월간',
        description: '월간 만보기 랭킹 기준으로 경품을 노출합니다.',
    },
]

const RANKING_BANNER_STATUS_OPTIONS = [
    {
        value: 1,
        title: '노출',
        description: '사용자 화면에 랭킹경품을 표시합니다.',
    },
    {
        value: 0,
        title: '미노출',
        description: '관리자에서만 보관하고 사용자 화면에는 표시하지 않습니다.',
    },
]

const getImageFileName = (imageFile) => {
    if (!imageFile || typeof imageFile !== 'object') {
        return ''
    }

    return imageFile.name || ''
}

function FormSection({ title, description, children }) {
    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {children}
            </Box>
        </Paper>
    )
}

function ChoiceCardLabel({ title, description }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </Box>
    )
}

// 카드형 선택 UI에서도 기존 폼 변경 핸들러가 받던 name/value 이벤트 계약을 유지한다.
const createFieldChangeEvent = (name, value) => ({
    target: {
        name,
        value,
    },
})

function SettingChoiceGroup({
    title,
    name,
    value,
    options,
    disabled,
    onChange,
    gridTemplateColumns = { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
}) {
    return (
        <FormControl fullWidth>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {title}
            </Typography>
            <Box
                role="radiogroup"
                aria-label={title}
                sx={{
                    display: 'grid',
                    gridTemplateColumns,
                    gap: 1,
                    mt: 1,
                }}
            >
                {options.map((option) => {
                    const checked = String(value) === String(option.value)

                    return (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={(
                                <Radio
                                    name={name}
                                    checked={checked}
                                    onChange={() => onChange(createFieldChangeEvent(name, option.value))}
                                    disabled={disabled}
                                />
                            )}
                            disabled={disabled}
                            sx={getChoiceCardSx(checked, disabled)}
                            label={(
                                <ChoiceCardLabel
                                    title={option.title}
                                    description={option.description}
                                />
                            )}
                        />
                    )
                })}
            </Box>
        </FormControl>
    )
}

function RankingBannerImageUploadPanel({
    fieldConfig,
    imageSrc,
    imageFile,
    disabled,
    onUploadClick,
}) {
    const imageFileName = getImageFileName(imageFile)

    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {fieldConfig.previewAlt}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    App 랭킹경품 화면에 노출되는 이미지를 등록합니다.
                </Typography>
            </Box>
            <Box sx={IMAGE_PREVIEW_FRAME_SX}>
                {imageSrc ? (
                    <Box
                        component="img"
                        src={imageSrc}
                        alt={`${fieldConfig.previewAlt} 미리보기`}
                        sx={IMAGE_PREVIEW_SX}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        이미지를 선택하면 미리보기가 표시됩니다.
                    </Typography>
                )}
            </Box>
            <FormControl fullWidth>
                <TextField
                    inputProps={{ accept: APP_RANKING_BANNER_IMAGE_FILE_ACCEPT }}
                    style={{ display: 'none' }}
                    name={fieldConfig.fieldName}
                    id={fieldConfig.inputId}
                    type="file"
                    disabled={disabled}
                    onChange={onUploadClick}
                />
                <Box component="label" htmlFor={fieldConfig.inputId} sx={{ display: 'block' }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        component="span"
                        startIcon={<Add />}
                        sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                        disabled={disabled}
                    >
                        {fieldConfig.uploadLabel}
                    </Button>
                </Box>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {imageFileName ? `선택한 파일: ${imageFileName}` : '저장 전 미리보기로 이미지가 올바른지 확인하세요.'}
            </Typography>
        </Paper>
    )
}

export default function NewAppRankingBanner() {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const listCurPage = locationState.curPage || 1
    const listSearchText = locationState.searchText || ''
    const listSearchSelect = locationState.searchSelect || { searchType: '' }
    const listSearch = location.search || ''
    const [saving, setSaving] = useState(false)
    const [params, setParams] = useState(() => createDefaultAppRankingBannerParams())
    const [image, setImage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [draftReady, setDraftReady] = useState(false)
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const initialDraftSnapshotRef = useRef('')
    const leaveGuardEnabled = hasPendingAppRankingBannerImage(params) && !saving && !navigationUnlocked
    const imageFieldConfigs = APP_RANKING_BANNER_IMAGE_SECTION_CONFIGS.create

    usePendingAppRankingBannerImageUnloadGuard(leaveGuardEnabled)

    useEffect(() => {
        const baseParams = createDefaultAppRankingBannerParams()
        const storedDraft = readAppRankingBannerDraft(NEW_APP_RANKING_BANNER_DRAFT_KEY)
        const restoredDraft = applyAppRankingBannerDraft({
            baseParams,
            draft: storedDraft,
        })

        initialDraftSnapshotRef.current = JSON.stringify(createAppRankingBannerDraftPayload({
            params: baseParams,
        }))

        setParams((prev) => ({
            ...prev,
            ...restoredDraft.params,
        }))
        setDraftReady(true)
    }, [])

    useEffect(() => {
        if (!draftReady) {
            return
        }

        // App 랭킹경품 등록은 목록 query를 건드리지 않고 폼 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppRankingBannerDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppRankingBannerDraft(NEW_APP_RANKING_BANNER_DRAFT_KEY)
            return
        }

        saveAppRankingBannerDraft(NEW_APP_RANKING_BANNER_DRAFT_KEY, nextDraft)
    }, [draftReady, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        moveToAppRankingBannerList({
            draftKey: NEW_APP_RANKING_BANNER_DRAFT_KEY,
            clearDraft: clearAppRankingBannerDraft,
            setNavigationUnlocked,
            history,
            listSearch,
            snackbarState,
            curPage: listCurPage,
            searchText: listSearchText,
            searchSelect: listSearchSelect,
        })
    }

    const handleBackClick = (event) => {
        handleAppRankingBannerBackNavigation({
            event,
            leaveGuardEnabled,
            draftKey: NEW_APP_RANKING_BANNER_DRAFT_KEY,
            clearDraft: clearAppRankingBannerDraft,
            confirmLeave: () => window.confirm(APP_RANKING_BANNER_IMAGE_LEAVE_MESSAGE),
            moveToList,
        })
    }

    const handleUpdateField = (e) => {
        setParams((prev) => applyAppRankingBannerFieldChange({
            params: prev,
            name: e.target.name,
            value: e.target.value,
        }))
    }

    const handleUploadClick = (e) => {
        handleAppRankingBannerImageUpload({
            event: e,
            setParams,
            setImage,
            onInvalidFile: (message = APP_RANKING_BANNER_IMAGE_FILE_ERROR_MESSAGE) => {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(message)
            },
        })
    }

    const createData = async (formData) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/appRankingBanner/create',
                data: formData,
                headers: { "Content-Type": "multipart/form-data" }
            })

            if (data.code === 200) {
                // 등록 후에도 이전 App 랭킹경품 목록 조건으로 그대로 복귀시킨다.
                moveToList({
                    severity: 'success',
                    message: data.message,
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('App 랭킹경품을 등록하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const { nextParams, errorMessage, formData } = prepareAppRankingBannerSubmit({
            params,
            requireImage: true,
            titleRequiredMessage: '경품명을 입력하세요.',
        })

        setParams(nextParams)

        if (errorMessage) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
            return
        }

        createData(formData)
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Prompt when={leaveGuardEnabled} message={APP_RANKING_BANNER_IMAGE_LEAVE_MESSAGE} />
                <Title>App 랭킹경품 등록</Title>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        {imageFieldConfigs.map((fieldConfig) => (
                            <Grid key={fieldConfig.inputId} item xs={12} md={5}>
                                <RankingBannerImageUploadPanel
                                    fieldConfig={fieldConfig}
                                    imageSrc={image}
                                    imageFile={params[fieldConfig.fieldName]}
                                    disabled={saving}
                                    onUploadClick={handleUploadClick}
                                />
                            </Grid>
                        ))}
                        <Grid item xs={12} md={7}>
                            <FormSection
                                title="경품 정보"
                                description="랭킹경품의 순위, 경품명, 이동 링크를 작성합니다."
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        required
                                        label="시작 순위"
                                        id="rank"
                                        name="rank"
                                        value={params.rank ?? ''}
                                        disabled={saving}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                                <FormControl fullWidth>
                                    <TextField
                                        required
                                        label="경품명"
                                        id="title"
                                        name="title"
                                        value={params.title || ''}
                                        disabled={saving}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                                <FormControl fullWidth>
                                    <TextField
                                        label="링크URL"
                                        id="url"
                                        name="url"
                                        value={params.url || ''}
                                        disabled={saving}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </FormSection>
                        </Grid>
                        <Grid item xs={12}>
                            <FormSection
                                title="경품 지급"
                                description="포인트·기프티콘 지급 규칙과 적용 기간을 설정합니다. 전시만 선택하면 지금처럼 이미지만 노출합니다."
                            >
                                <AppRankingBannerRewardFields
                                    params={params}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                />
                            </FormSection>
                        </Grid>
                        <Grid item xs={12}>
                            <FormSection
                                title="노출 설정"
                                description="랭킹 기준과 사용자 화면 노출 여부를 함께 설정합니다."
                            >
                                <SettingChoiceGroup
                                    title="구분"
                                    name="type"
                                    value={params.type || 1}
                                    options={RANKING_BANNER_TYPE_OPTIONS}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                    gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }}
                                />
                                <SettingChoiceGroup
                                    title="노출 여부"
                                    name="status"
                                    value={params.status ?? 1}
                                    options={RANKING_BANNER_STATUS_OPTIONS}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                />
                                <SettingChoiceGroup
                                    title="노출 국가"
                                    name="exposeRegion"
                                    value={params.exposeRegion || APP_RANKING_BANNER_EXPOSE_REGIONS.ALL}
                                    options={APP_RANKING_BANNER_EXPOSE_REGION_OPTIONS}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                    gridTemplateColumns={{ xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }}
                                />
                            </FormSection>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup path="/appRankingBanners" search={listSearch} visible={true} curPage={listCurPage} searchText={listSearchText} searchSelect={listSearchSelect} submitDisabled={saving} submitLabel={saving ? '저장 중...' : '저장'} onBackClick={handleBackClick} />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
